import {
  GROUP_CREATE,
  GROUP_CREATE_FAIL,
  GROUP_CREATE_SUCCESS,
  GROUP_DELETE,
  GROUP_DELETE_FAIL,
  GROUP_DELETE_SUCCESS,
  GROUP_GET,
  GROUP_GET_FAIL,
  GROUP_GET_SUCCESS,
  GROUP_UPDATE,
  GROUP_UPDATE_FAIL,
  GROUP_UPDATE_SUCCESS,
  GROUPS_LOAD,
  GROUPS_LOAD_FAIL,
  GROUPS_LOAD_SUCCESS,
  GROUP_BUSINESS_SHARE_UPDATE_SUCCESS,
  LOAD_GROUP_BUSINESS_SHARES_SUCCESS,
} from '@disco/constants/ActionTypes'
import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import { getGroupSlug } from '@disco/utils/groups'
import find from 'lodash/find'
import uniqBy from 'lodash/uniqBy'
import { createReducer } from 'redux-create-reducer'
import type { Group } from 'types/group'
const initialState = {
  isFetching: false,
  isFetched: false,
  error: null,
  groups: [] as any[],
}

function generateCustomLibraries(groups: Group[]): Group[] {
  return groups.map(group => {
    return generateCustomLibrary(group)
  })
}

export function generateCustomLibrary(group: Group): Group {
  return {
    ...group,
    slug: group.slug || getGroupSlug(group),
    panes: [
      {
        url:
          group.group_type === CUSTOM_CHANNEL
            ? `${window.host}/api/browse/`
            : `${window.host}/api/browse/?type=playlist&share=1`,
        name: group.name,
        internal_name: group.internal_name,
        index: 0,
        groupId: group.id,
        groupType: group.group_type,
        type: 'searchResults',
      },
    ],
  }
}

function getUpdatedGroups(groups: Group[], updatedGroup: Group): Group[] {
  return groups.map(group => {
    if (group.id === updatedGroup.id) {
      return updatedGroup
    }

    return group
  })
}

export type State = typeof initialState
export const groupsReducer = createReducer<State, any>(initialState, {
  [GROUPS_LOAD](state) {
    return { ...state, isFetching: true }
  },

  [GROUPS_LOAD_SUCCESS](state, { groups, refresh }) {
    const groupsUpdate = refresh
      ? [
          ...state.groups.filter(
            stateGroup => !groups.find(g => g.id === stateGroup.id),
          ), // exclude groups present in refreshed data
          ...generateCustomLibraries(groups),
        ]
      : generateCustomLibraries(groups)
    return {
      ...state,
      isFetching: false,
      isFetched: true,
      error: null,
      groups: groupsUpdate,
    }
  },

  [GROUPS_LOAD_FAIL](state, action) {
    return { ...state, isFetching: false, error: action.error }
  },

  [GROUP_GET](state) {
    return { ...state, isFetching: true }
  },

  [GROUP_GET_SUCCESS](state, action) {
    const { groups } = state
    const getItemIndex = groups.indexOf(
      find(groups, {
        id: action.group.id,
      }),
    )
    const customLibraryGroup = generateCustomLibrary(action.group)

    if (getItemIndex > -1) {
      return {
        ...state,
        isFetching: false,
        error: null,
        groups: [
          ...groups.slice(0, getItemIndex),
          customLibraryGroup,
          ...groups.slice(getItemIndex + 1),
        ],
      }
    } else {
      return {
        ...state,
        isFetching: false,
        error: null,
        groups: [...groups, customLibraryGroup],
      }
    }
  },

  [GROUP_GET_FAIL](state, action) {
    return { ...state, isFetching: false, error: action.error }
  },

  [GROUP_DELETE](state) {
    return { ...state, isFetching: true }
  },

  [GROUP_DELETE_SUCCESS](state, action) {
    const { groups } = state
    const deletedItemIndex = groups.indexOf(
      find(groups, {
        id: action.group.id,
      }),
    )
    if (deletedItemIndex < 0) return state
    return {
      ...state,
      isFetching: false,
      error: null,
      groups: [
        ...groups.slice(0, deletedItemIndex),
        ...groups.slice(deletedItemIndex + 1),
      ],
    }
  },

  [GROUP_DELETE_FAIL](state, action) {
    return { ...state, isFetching: false, error: action.error }
  },

  [GROUP_UPDATE](state) {
    return { ...state, isFetching: true }
  },

  [GROUP_UPDATE_SUCCESS](state, action) {
    const { groups } = state
    const updatedItemIndex = groups.indexOf(
      find(groups, {
        id: action.group.id,
      }),
    )
    if (updatedItemIndex < 0) return state
    return {
      ...state,
      isFetching: false,
      error: null,
      groups: [
        ...groups.slice(0, updatedItemIndex),
        generateCustomLibrary(action.group),
        ...groups.slice(updatedItemIndex + 1),
      ],
    }
  },

  [GROUP_UPDATE_FAIL](state, action) {
    return { ...state, isFetching: false, error: action.error }
  },

  [GROUP_CREATE](state) {
    return { ...state, isFetching: true }
  },

  [GROUP_CREATE_SUCCESS](state, action) {
    const { groups } = state
    return {
      ...state,
      isFetching: false,
      error: null,
      groups: [...groups, generateCustomLibrary(action.group)],
    }
  },

  [GROUP_CREATE_FAIL](state, action) {
    return { ...state, isFetching: false, error: action.error }
  },

  [LOAD_GROUP_BUSINESS_SHARES_SUCCESS](state, action) {
    const { groups } = state
    const { groupId } = action
    const group = find(groups, {
      id: groupId,
    })
    if (!group) return state
    const updatedGroups = getUpdatedGroups(groups, {
      ...group,
      shared_with_businesses: action.invites,
    })
    return { ...state, groups: updatedGroups }
  },

  [GROUP_BUSINESS_SHARE_UPDATE_SUCCESS](state, action) {
    const { groupId, inviteId, invites } = action
    const { groups } = state
    const group = find(groups, {
      id: groupId,
    })
    if (!group) return state
    let businessList = group.shared_with_businesses || []
    if (invites) businessList = uniqBy([...businessList, invites], 'id')
    if (inviteId)
      businessList = businessList.filter(invite => invite.id !== inviteId)
    const updatedGroups = getUpdatedGroups(groups, {
      ...group,
      shared_with_businesses: businessList,
    })
    return { ...state, groups: updatedGroups }
  },
})
